import { memo, useMemo } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { BiGridVertical } from 'react-icons/bi';
import { currencySymbol } from '../../constants/currencies';

// Same palette as jobExpensePieChart.jsx for visual consistency
const PALETTE = [
    '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444',
    '#06b6d4', '#f97316', '#ec4899', '#84cc16', '#6366f1',
];

const TOOLTIP_STYLE = {
    backgroundColor: 'var(--bs-body-bg)',
    border: '1px solid var(--bs-border-color)',
    borderRadius: '0.5rem',
    color: 'var(--bs-body-color)',
};

// `currency` is the company base currency (figures are server-converted into it).
export default memo(function ExpenseBreakdownChart({ data, currency = 'GBP' }) {
    const sym = currencySymbol(currency);
    const fmt = (v) => `${sym}${v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // API shape: { categoryName, totalAmount }
    const chartData = useMemo(
        () => (data ?? []).map(d => ({ name: d.categoryName, value: d.totalAmount })),
        [data],
    );

    if (!chartData.length) {
        return (
            <div className="surface h-100 d-flex flex-column">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h3 className="fw-semibold fs-5 mb-0 drag-handle">Expenses by Category</h3>
                    <BiGridVertical className="drag-handle text-muted flex-shrink-0 d-none d-md-block" style={{ opacity: 0.4, fontSize: '1.1rem' }} />
                </div>
                <div className="flex-grow-1 d-flex align-items-center justify-content-center text-muted small">
                    No expense data yet
                </div>
            </div>
        );
    }

    return (
        <div className="surface h-100 recharts-outline-none d-flex flex-column">
            <div className="d-flex align-items-center justify-content-between mb-0">
                <h3 className="fw-semibold fs-5 mb-0 drag-handle">Expenses by Category</h3>
                <BiGridVertical className="drag-handle text-muted flex-shrink-0 d-none d-md-block" style={{ opacity: 0.4, fontSize: '1.1rem' }} />
            </div>
            <div className="flex-grow-1" style={{ minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius="40%"
                            outerRadius="65%"
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) =>
                                `${name}: ${(percent * 100).toFixed(0)}%`
                            }
                            labelLine={{ stroke: 'var(--bs-secondary-color)', strokeWidth: 1 }}
                            isAnimationActive={false}
                            fontSize={11}
                        >
                            {chartData.map((_, i) => (
                                <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                            ))}
                        </Pie>
                        <Legend
                            layout="horizontal"
                            verticalAlign="bottom"
                            align="center"
                            wrapperStyle={{ fontSize: '0.75rem' }}
                        />
                        <Tooltip
                            formatter={(v) => [fmt(v)]}
                            contentStyle={TOOLTIP_STYLE}
                            labelStyle={{ color: 'var(--bs-body-color)', fontWeight: 600 }}
                            itemStyle={{ color: 'var(--bs-body-color)' }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
});
